import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useUser } from '../../config/UserContext';
import Refresh from '../Icons/Refresh';
import styles from './EmojiPicker.module.scss';

const EMOJI_OPTIONS = [
  // Faces & emotions
  '💛', '😊', '🥰', '😍', '🤩', '😎', '🥳', '😤', '🤯', '😴',
  '🫠', '😇', '🤗', '😌', '🥹', '😭', '😏', '🙃', '🥺', '😵',
  '🤤', '😈', '🤡', '👽', '👾', '🤠', '🧐', '🤨', '😬', '🫡',
  // Gestures & body
  '👏', '🙌', '🤙', '🤟', '✊', '💪', '👂', '🫶', '🤝', '🫂',
  '👀', '🫦', '🙏', '💅', '🦾',
  // Music
  '🎵', '🎶', '🎸', '🎹', '🎺', '🎻', '🥁', '🎤', '🎧', '🎼',
  '🪗', '🪘', '🎷', '📻', '💿', '📀',
  // Nature & elements
  '🔥', '✨', '💫', '⚡', '🌊', '🌙', '⭐', '🌟', '🌈', '🌸',
  '🌺', '🌻', '🍄', '🌋', '🌀', '🪐', '☄️', '🌿',
  // Objects & symbols
  '💎', '🪄', '🔮', '🧲', '🪩', '🎭', '🎪', '🎠', '🃏', '🎯',
  '🏆', '🪙', '🔑', '🗝️', '💌', '📡',
  // Animals & creatures
  '🦋', '🐬', '🦁', '🐺', '🦊', '🐙', '🦄', '🦅', '🐉', '🦈',
  '🐝', '🦚', '🦜', '🐸',
  // Misc fun
  '💃', '🕺', '🆒', '🪨', '🏔️', '🛸', '🎉', '🪅', '🌊', '🧿',
  '💥', '🫧', '🍷', '🧃', '🍉', '🫐',
];

const PICKER_GAP = 8; // px

const getRandomEmojis = () => {
  const shuffled = [...EMOJI_OPTIONS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 7);
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const pickerVariants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
};

const EmojiPicker = ({ onEmojiSelect, currentEmoji }) => {
  const { currentUser } = useUser();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [openUpwards, setOpenUpwards] = useState(false);
  const [randomEmojis, setRandomEmojis] = useState(getRandomEmojis());
  const [isSpinning, setIsSpinning] = useState(false);
  const [settledSlots, setSettledSlots] = useState(() => new Set());
  const emojiButtonRef = useRef(null);
  const pickerRef = useRef(null);
  const spinCleanupRef = useRef(null);

  const handleRefresh = useCallback(() => {
    if (isSpinning) return;
    setIsSpinning(true);
    setSettledSlots(new Set());

    const finalEmojis = getRandomEmojis();

    // Rapidly cycle all slots
    const intervalId = setInterval(() => {
      setRandomEmojis(
        Array.from({ length: 7 }, () =>
          EMOJI_OPTIONS[Math.floor(Math.random() * EMOJI_OPTIONS.length)]
        )
      );
    }, 80);

    // Lock each slot in one by one, staggered
    const timeouts = finalEmojis.map((emoji, i) =>
      setTimeout(() => {
        setRandomEmojis((prev) => {
          const next = [...prev];
          next[i] = emoji;
          return next;
        });
        setSettledSlots((prev) => new Set(prev).add(i));
        if (i === finalEmojis.length - 1) {
          clearInterval(intervalId);
          setIsSpinning(false);
        }
      }, 500 + i * 110)
    );

    spinCleanupRef.current = () => {
      clearInterval(intervalId);
      timeouts.forEach(clearTimeout);
    };
  }, [isSpinning]);

  // Clean up on unmount
  useEffect(() => () => spinCleanupRef.current?.(), []);

  // When button is clicked, measure and position, then show picker
  const handleEmojiButtonClick = useCallback(() => {
    if (!currentUser) return;

    // Measure and position immediately
    if (emojiButtonRef.current) {
      const buttonRect = emojiButtonRef.current.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Estimate picker height (you can adjust this based on your actual picker size)
      const estimatedPickerHeight = 160; // Reduced from 200 to be more accurate
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const shouldOpenUpwards =
        spaceBelow < estimatedPickerHeight + PICKER_GAP &&
        spaceAbove > estimatedPickerHeight;

      setOpenUpwards(shouldOpenUpwards);
      setPickerPosition({
        top: shouldOpenUpwards
          ? buttonRect.top - estimatedPickerHeight + 20 // Position picker to overlap slightly with button
          : buttonRect.bottom + PICKER_GAP,
        left: Math.max(0, buttonRect.left - 8), // Minimal left offset for better centering
      });
    }

    setShowEmojiPicker(true);
  }, [currentUser]);

  // Handle emoji selection
  const handleEmojiSelect = useCallback(
    (emoji) => {
      if (!currentUser) return;
      onEmojiSelect(emoji);
      setShowEmojiPicker(false);
    },
    [onEmojiSelect, currentUser]
  );

  const handleBackdropTouch = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEmojiPicker(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target) &&
        !emojiButtonRef.current.contains(e.target)
      ) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showEmojiPicker]);

  return (
    <div className={styles.emojiPickerContainer}>
      <button
        ref={emojiButtonRef}
        className={`${styles.emojiButton} ${currentEmoji ? styles.active : ''} ${
          !currentUser ? styles.disabled : ''
        }`}
        onClick={handleEmojiButtonClick}
        disabled={!currentUser}
      >
        {currentEmoji || '😊'}
      </button>

      {showEmojiPicker &&
        createPortal(
          <AnimatePresence>
            <motion.div
              key="backdrop"
              className={styles.backdrop}
              onClick={handleBackdropTouch}
              onTouchStart={handleBackdropTouch}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={backdropVariants}
              transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998,
              }}
            />
            <motion.div
              key="picker"
              ref={pickerRef}
              className={`${styles.emojiPicker} ${openUpwards ? styles.openUpwards : ''}`}
              style={{
                top: `${pickerPosition.top}px`,
                left: `${pickerPosition.left}px`,
                position: 'fixed',
                zIndex: 9999,
              }}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={pickerVariants}
              transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className={styles.emojiOptions}>
                {randomEmojis.map((emoji, i) => (
                  <div
                    key={i}
                    className={`${styles.emojiOption} ${
                      emoji === currentEmoji ? styles.selected : ''
                    }`}
                    onClick={() => !isSpinning && handleEmojiSelect(emoji)}
                  >
                    <div className={styles.emojiSlot}>
                      <AnimatePresence mode="popLayout" initial={false}>
                        <motion.span
                          key={`${i}-${emoji}`}
                          className={styles.emoji}
                          initial={{ y: -20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 20, opacity: 0 }}
                          transition={
                            settledSlots.has(i)
                              ? { duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }
                              : { duration: 0.06, ease: 'linear' }
                          }
                        >
                          {emoji}
                        </motion.span>
                      </AnimatePresence>
                    </div>
                  </div>
                ))}

                {/* Refresh button in the 8th position */}
                <div
                  className={`${styles.emojiOption} ${styles.refreshOption}`}
                  onClick={handleRefresh}
                >
                  <motion.div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    animate={{ rotate: isSpinning ? 360 : 0 }}
                    transition={
                      isSpinning
                        ? { duration: 0.5, repeat: Infinity, ease: 'linear' }
                        : { duration: 0.3 }
                    }
                  >
                    <Refresh />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default EmojiPicker;
